import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from "@/lib/utils";
import {
  LayoutGrid,
  LineChart as LineChartIcon,
  Cpu,
  Siren,
  ChevronRight
} from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
  Legend
} from 'recharts';

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } }
};

const staggerChildren = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

// Mock data for graphs - simplified and made more dynamic
const generateFleetData = () => Array.from({ length: 7 }, (_, i) => {
  const day = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][i];
  return {
    name: day,
    distance: Math.floor(80 + Math.random() * 120),
    vehicles: Math.floor(180 + Math.random() * 50)
  };
});

const generateIoTData = () => Array.from({ length: 6 }, (_, i) => {
  const hour = i * 4;
  return {
    time: `${String(hour).padStart(2, '0')}:00`,
    temp: Math.floor(20 + Math.random() * 8),
    humidity: Math.floor(50 + Math.random() * 25)
  };
});

const edgeCpuData = [{ name: 'Load', value: 67 }, { name: 'Free', value: 33 }];
const edgeStorageData = [{ name: 'Used', value: 75 }, { name: 'Free', value: 25 }];
const alertCategories = [
  { name: 'Critical', value: 2, color: 'hsl(var(--destructive))' },
  { name: 'Warning', value: 5, color: 'hsl(var(--warning))' },
  { name: 'Info', value: 10, color: 'hsl(var(--info))' },
];
const CHART_COLORS = {
  primary: 'hsl(var(--primary))',
  secondary: 'hsl(var(--secondary))',
  muted: 'hsl(var(--muted-foreground))',
  border: 'hsl(var(--border))',
  tooltipBg: 'hsl(var(--popover))',
  tooltipFg: 'hsl(var(--popover-foreground))'
};

export default function AnalyticsDashboardPreview({ viewport, isRTL }) {
  const { t } = useTranslation();
  const { isMobile = false } = viewport || {};

  const [activeTab, setActiveTab] = useState('fleet');
  const [fleetData, setFleetData] = useState(generateFleetData());
  const [iotData, setIotData] = useState(generateIoTData());

  useEffect(() => {
    const interval = setInterval(() => {
      setFleetData(generateFleetData());
      setIotData(generateIoTData());
    }, 5000); // Refresh data every 5 seconds for demo
    return () => clearInterval(interval);
  }, []);

  const dashboardTabs = [
    { id: 'fleet', name: t('homepage.dashboard.tabs.fleet', 'Fleet Analytics'), icon: LayoutGrid },
    { id: 'iot', name: t('homepage.dashboard.tabs.iot', 'IoT Monitoring'), icon: LineChartIcon },
    { id: 'edge', name: t('homepage.dashboard.tabs.edge', 'Edge Devices'), icon: Cpu },
    { id: 'alerts', name: t('homepage.dashboard.tabs.alerts', 'System Alerts'), icon: Siren }
  ];

  const commonTooltipProps = {
    contentStyle: {
      backgroundColor: CHART_COLORS.tooltipBg,
      borderColor: CHART_COLORS.border,
      borderRadius: 'var(--radius)',
      color: CHART_COLORS.tooltipFg,
      padding: '0.5rem 0.75rem',
      boxShadow: 'var(--shadow-md)'
    },
    itemStyle: { color: CHART_COLORS.tooltipFg, fontSize: '0.8rem' },
    labelStyle: { color: CHART_COLORS.muted, marginBottom: '0.25rem', fontSize: '0.75rem', fontWeight: '500' },
    cursor: { fill: 'hsl(var(--primary)/0.05)' }
  };

  const renderDashboardContent = (tabId) => {
    switch (tabId) {
      case 'fleet':
        return (
          <ResponsiveContainer width="100%" height={isMobile ? 280 : 320}>
            <BarChart data={fleetData} margin={{ top: 5, right: isMobile ? 0 : 10, left: isMobile ? -30 : -20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={CHART_COLORS.border + '80'} />
              <XAxis dataKey="name" fontSize={11} tickLine={false} axisLine={{stroke: CHART_COLORS.border}} stroke={CHART_COLORS.muted} />
              <YAxis fontSize={11} tickLine={false} axisLine={{stroke: CHART_COLORS.border}} stroke={CHART_COLORS.muted} />
              <Tooltip {...commonTooltipProps} />
              <Legend verticalAlign="top" height={36} iconSize={10} wrapperStyle={{fontSize: "0.8rem"}}/>
              <Bar dataKey="distance" fill={CHART_COLORS.primary} radius={[3, 3, 0, 0]} barSize={isMobile ? 10 : 12} name={t('common.distance', 'Distance (km)')}/>
              <Bar dataKey="vehicles" fill={CHART_COLORS.secondary} radius={[3, 3, 0, 0]} barSize={isMobile ? 10 : 12} name={t('common.vehicles', 'Active Vehicles')}/>
            </BarChart>
          </ResponsiveContainer>
        );
      case 'iot':
        return (
          <ResponsiveContainer width="100%" height={isMobile ? 280 : 320}>
            <LineChart data={iotData} margin={{ top: 5, right: isMobile ? 5 : 15, left: isMobile ? -30 : -20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={CHART_COLORS.border + '80'} />
              <XAxis dataKey="time" fontSize={11} tickLine={false} axisLine={{stroke: CHART_COLORS.border}} stroke={CHART_COLORS.muted} />
              <YAxis fontSize={11} tickLine={false} axisLine={{stroke: CHART_COLORS.border}} stroke={CHART_COLORS.muted} />
              <Tooltip {...commonTooltipProps} />
              <Legend verticalAlign="top" height={36} iconSize={10} wrapperStyle={{fontSize: "0.8rem"}}/>
              <Line type="monotone" dataKey="temp" stroke={CHART_COLORS.primary} strokeWidth={2} dot={{ r: 2 }} activeDot={{ r: 5 }} name={t('common.temperature', 'Temp (°C)')}/>
              <Line type="monotone" dataKey="humidity" stroke={CHART_COLORS.secondary} strokeWidth={2} dot={{ r: 2 }} activeDot={{ r: 5 }} name={t('common.humidity', 'Humidity (%)')}/>
            </LineChart>
          </ResponsiveContainer>
        );
      case 'edge':
        return (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6 items-center h-full">
            {[ { titleKey: 'cpu', data: edgeCpuData, color: CHART_COLORS.primary }, 
               { titleKey: 'storage', data: edgeStorageData, color: CHART_COLORS.secondary } ].map(chart => (
              <div key={chart.titleKey} className="flex flex-col items-center">
                <p className="text-xs text-muted-foreground mb-1">{t(`homepage.dashboard.edge.${chart.titleKey}`, chart.titleKey)}</p>
                <ResponsiveContainer width="100%" height={isMobile ? 120 : 140}>
                  <PieChart>
                    <Pie data={chart.data} dataKey="value" nameKey="name" cx="50%" cy="50%" 
                         outerRadius={isMobile ? 45:55} innerRadius={isMobile? 25:30} 
                         labelLine={false} 
                         label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} 
                         fontSize={isMobile? 9:10} fontWeight={500}
                    >
                      <Cell fill={chart.color} />
                      <Cell fill={CHART_COLORS.border + '40'} />
                    </Pie>
                    <Tooltip {...commonTooltipProps} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ))}
          </div>
        );
      case 'alerts':
        return (
          <div className="space-y-2.5 md:space-y-3 py-2">
            {alertCategories.map((alert, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: isRTL ? 20 : -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.08, duration: 0.3 }}
                className="flex items-center justify-between p-3 rounded-md"
                style={{ backgroundColor: `color-mix(in srgb, ${alert.color} 15%, transparent)` }}
              >
                <span className="font-medium text-xs sm:text-sm" style={{color: alert.color}}>{alert.name}</span>
                <span className="font-bold text-sm sm:text-base" style={{color: alert.color}}>{alert.value}</span>
              </motion.div>
            ))}
          </div>
        );
      default: return null;
    }
  };

  return (
    <section
      id="dashboard-preview"
      className="relative py-20 md:py-28 lg:py-32 bg-muted/30 overflow-hidden"
    >
      <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={staggerChildren}
          className="max-w-3xl mx-auto text-center mb-12 md:mb-16"
        >
          <motion.div variants={fadeInUp}>
            <Badge
              variant="outline"
              className="mb-3 px-3 py-1 text-xs font-medium rounded-full border-primary/30 bg-primary/10 text-primary"
            >
              {t('homepage.dashboard.badge', 'Data-Driven Decisions')}
            </Badge>
          </motion.div>
          <motion.div variants={fadeInUp}>
            <h2 className="text-3xl md:text-4xl font-bold mb-4 tracking-tight text-foreground">
              {t('homepage.dashboard.title', 'Visualize Your Success')}
            </h2>
          </motion.div>
          <motion.div variants={fadeInUp}>
            <p className="text-md md:text-lg text-muted-foreground max-w-xl mx-auto">
              {t('homepage.dashboard.subtitle', 'Explore interactive dashboards that provide deep insights into your fleet, IoT devices, edge performance, and critical alerts.')}
            </p>
          </motion.div>
        </motion.div>

        <motion.div variants={fadeInUp} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.1 }}>
          <Card className="w-full max-w-4xl mx-auto shadow-xl shadow-primary/5 border-border/20 bg-background/80 backdrop-blur-lg rounded-xl overflow-hidden">
            <Tabs value={activeTab} onValueChange={setActiveTab} dir={isRTL ? 'rtl' : 'ltr'} className="w-full">
              <CardHeader className="p-0 border-b border-border/50">
                <TabsList className={cn(
                  "grid w-full rounded-none h-auto p-1",
                  `grid-cols-${dashboardTabs.length}`,
                  isMobile && "grid-cols-2"
                )}>
                  {dashboardTabs.map((tab) => {
                    const Icon = tab.icon;
                    return (
                      <TabsTrigger
                        key={tab.id}
                        value={tab.id}
                        className={cn(
                          "items-center justify-center gap-1.5 sm:gap-2 px-2 py-2.5 sm:px-3 sm:py-3 h-auto text-xs sm:text-sm font-medium rounded-none transition-all",
                          "data-[state=active]:bg-primary/10 data-[state=active]:text-primary data-[state=active]:shadow-[inset_0_-2px_0_0_hsl(var(--primary))]",
                          "hover:bg-muted/50 data-[state=inactive]:text-muted-foreground",
                          "flex flex-row"
                        )}
                      >
                        <Icon size={isMobile ? 16 : 18} />
                        <span>{tab.name}</span>
                      </TabsTrigger>
                    );
                  })}
                </TabsList>
              </CardHeader>
              <CardContent className="p-3 sm:p-4 md:p-5 min-h-[300px] sm:min-h-[350px]">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={activeTab}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.25, ease: 'easeInOut' }}
                    className="w-full h-full"
                  >
                    {renderDashboardContent(activeTab)}
                  </motion.div>
                </AnimatePresence>
              </CardContent>
            </Tabs>
             <CardDescription className="p-3 sm:p-4 text-center text-xs text-muted-foreground border-t border-border/50">
                {t('homepage.dashboard.demoNotice', 'Live dashboard preview. Data shown is for demonstration purposes.')}
            </CardDescription>
          </Card>
        </motion.div>

        <motion.div 
            variants={fadeInUp} initial="hidden" 
            whileInView="visible" viewport={{ once: true, amount: 0.5 }}
            className="text-center mt-10 md:mt-12"
        >
            <Button size="lg" asChild className="group">
                <a href="/dashboard">
                    {t('homepage.dashboard.cta', 'Explore Full Dashboard')}
                    <ChevronRight size={20} className={cn("ml-2 transition-transform group-hover:translate-x-1", isRTL && "ml-0 mr-2 group-hover:-translate-x-1")} />
                </a>
            </Button>
        </motion.div>

      </div>
    </section>
  );
}
